import * as moment from 'moment';
import { useMemo, useState } from 'react';
import { IConnectToDonorStatus, IDonorConnect, IRole, IUser } from '@nkr/models/index';
import { DashDonationType } from '@nkr/enums/index';
import { BodyScrollEvent, CellClickedEvent, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, RowNode, ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { FormControl, Glyphicon, Label } from 'react-bootstrap';
import { GridWrapper } from './styles';
import { Utility } from '@nkr/services/utility';
import { donorConnectTabs, ICounselor } from './models';
import { AuthRole } from '@nkr/enums/auth-role';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import MentorDropdown from './mentor-dropdown';

interface INextFollowupDateRendererProps extends ICellRendererParams { statusList: IConnectToDonorStatus[] };
interface IStatusRendererProps extends ICellRendererParams { statusList: IConnectToDonorStatus[], onStatusChange: (id: number, statusId: number) => void };
interface IAssignedUserRendererProps extends ICellRendererParams { roles: IRole[], userList: ICounselor[], onAssignedCounselorChange: (id: number, userId: number) => void };
interface IDupesRendererProps extends ICellRendererParams { onShowDuplicates: (registrationId: number) => void };

interface IProps {
    list: IDonorConnect[],
    statusList: IConnectToDonorStatus[],
    userList: ICounselor[],
    user: IUser,
    currentTab: string,
    onViewRegistration: (id: number, centerId: number, recipientCenterId: number) => void,
    onStatusChange: (id: number, statusId: number) => void,
    onAssignedCounselorChange: (id: number, userId: number) => void,
    onShowDuplicates: (registrationId: number) => void
}

const Grid = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const handleConnectToDonorStatusChange = (value: number, params: IStatusRendererProps) => {

        if (value === 11) {
            let { id, centerId, recipientCenterId } = (params.data as IDonorConnect);
            
            // need a property to pass the selected statusId
            props.onViewRegistration(id, centerId, recipientCenterId);
            return;
        }

        params.onStatusChange(params.data.id, value);
    }

    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onBodyScroll = (e: BodyScrollEvent) => {
        if (menuDisplayed) {
            document.body.click();
            setMenuDisplayed(false);
        }
    }

    const handleMenuToggle = (show: boolean) => {
        setMenuDisplayed(show);
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const cols = [
        {
            colId: 'entrySource',
            field: 'entrySource',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Source</SortableHeaderComponent>,
            width: 110,
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IDonorConnect;
                return data.isClm ? `CLM: ${data.entrySource}` : data.entrySource;
            },
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IDonorConnect;
                let source = data.isClm ? `CLM: ${data.entrySource}` : data.entrySource;

                if (data.entryReferrerSrcCode == 'nutcracker') {
                    return <Label bsStyle='warning' style={{ fontSize: '100%' }} title={source}>{source}</Label>
                }

                return <div title={source}>{source}</div>;
            },
            comparator: Utility.stringSortComparator,
        },
        {
            colId: 'hasNutcracker',
            field: 'hasNutcracker',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Nut</SortableHeaderComponent>,
            width: 40,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IDonorConnect;
                return data.hasNutcracker ? <Glyphicon glyph="ok" style={{ fontSize: 16 }} /> : <>&nbsp;</>;
            }
        },
        {
            colId: 'firstName',
            field: 'firstName',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>First Name</SortableHeaderComponent>,
            width: 80,
            suppressSizeToFit: true,
            comparator: Utility.stringSortComparator,
            sortable: true
        },
        {
            colId: 'lastName',
            field: 'lastName',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Last Name</SortableHeaderComponent>,
            width: 80,
            suppressSizeToFit: true,
            comparator: Utility.stringSortComparator,
            sortable: true
        },
        {
            colId: 'preferredLanguage',
            field: 'preferredLanguage',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Preferred<br />Language</SortableHeaderComponent>,
            width: 110,
        },
        {
            colId: 'donationTypeAbbr',
            field: 'donationTypeAbbr',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Type</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IDonorConnect;

                if (data.donationTypeId == DashDonationType.Liver) {
                    return <Label bsStyle='warning' style={{ fontSize: '100%' }} title={params.data.donationType}>{params.data.donationTypeAbbr}</Label>
                }

                return <div title={params.data.donationType}>{params.data.donationTypeAbbr}</div>;
            },
            width: 65,
            cellStyle: { padding: 0 }
        },
        {
            colId: 'registrationDate',
            field: 'registrationDate',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Registration<br />Date</SortableHeaderComponent>,
            width: 120,
            comparator: Utility.dateComparator
        },
        {
            colId: 'donationDate',
            field: 'donationDate',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donation<br />Date</SortableHeaderComponent>,
            width: 120,
            hide: props.currentTab == donorConnectTabs.UNASSIGNED,
            comparator: Utility.dateComparator
        },
        {
            colId: 'phone',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Phone</SortableHeaderComponent>,
            width: 120,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IDonorConnect;

                if (data.phoneNumber != '')
                    return (
                        <>
                            <div>{data.phoneNumber}</div>
                            {data.phoneNumberSecondary != '' && (
                                <div>{data.phoneNumberSecondary}</div>
                            )}
                        </>
                    );

                return <div>--</div>;
            },
            sortable: true
        },
        {
            colId: 'candidateStatus',
            field: 'candidateStatus',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Candidate<br />Status</SortableHeaderComponent>,
            width: 200,
            // sortable: false
        },
        {
            colId: 'connectToDonorNextFollowUpDate',
            field: 'connectToDonorNextFollowUpDate',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Next<br />Follow-Up</SortableHeaderComponent>,
            width: 135,
            sort: 'asc',
            cellRendererParams: { statusList: props.statusList },
            cellRendererFramework: (params: INextFollowupDateRendererProps) => {
                let data = params.data as IDonorConnect;

                let status = params.statusList.find(s => s.id == data.connectToDonorStatusId);

                let nextFollowUpDate = moment(params.value, 'MM/DD/YYYY', true);

                if (status.isClosed == false && nextFollowUpDate.isBefore(moment(), 'day')) {
                    return <Label bsStyle='danger' style={{ fontSize: '100%' }}>{params.value}</Label>
                }

                if (status.isClosed == false && nextFollowUpDate.isAfter(moment(), 'day')) {
                    return <Label bsStyle='success' style={{ fontSize: '100%' }}>{params.value}</Label>
                }

                if (status.isClosed == false && nextFollowUpDate.isSame(moment(), 'day')) {
                    return <Label bsStyle='warning' style={{ fontSize: '100%' }}>{params.value}</Label>
                }

                if (status.isClosed) {
                    return <Label style={{ fontSize: '100%' }}>{params.value}</Label>
                }

                return null;
            },
            comparator: Utility.dateComparator
        },
        {
            colId: 'connectToDonorMilestone',
            field: 'connectToDonorMilestone',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Next<br />Milestone</SortableHeaderComponent>,
            width: 220,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IDonorConnect;
                return <div>{data.connectToDonorNextMilestoneDescription}</div>
            }
        },
        {
            colId: 'connectToDonorStatus',
            field: 'connectToDonorStatus',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Status</SortableHeaderComponent>,
            width: 240,
            cellClass: 'donor-connect-drop-down',
            cellRendererParams: { statusList: props.statusList, onStatusChange: props.onStatusChange },
            cellRendererFramework: (params: IStatusRendererProps) => {
                let data = params.data as IDonorConnect;

                return (
                    <>
                        <FormControl
                            componentClass='select'
                            bsSize='sm'
                            value={data.connectToDonorStatusId}
                            onChange={
                                (e: React.FormEvent<FormControl & HTMLSelectElement>) => 
                                    handleConnectToDonorStatusChange(Number(e.currentTarget.value), params) 
                            }
                        >
                            {params.statusList.map(item =>
                                <option key={'st-' + item.id} value={item.id}>{item.description}</option>
                            )}
                        </FormControl>
                    </>
                )
            },
            sortable: true
        },
        {
            colId: 'connectToDonorAssignedUserId',
            field: 'connectToDonorAssignedUserId',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Assigned<br />Mentor</SortableHeaderComponent>,
            width: 240,
            cellClass: 'action-menu',
            cellRendererParams: { roles: props.user.roles, userList: props.userList, onAssignedCounselorChange: props.onAssignedCounselorChange },
            cellStyle: { overflow: 'visible' },
            cellRendererFramework: (params: IAssignedUserRendererProps) => {
                let data = params.data as IDonorConnect;
                let allowChange = params.roles.find(x => x.id == AuthRole.DonorConnectAccessAll) != null || props.user.group.isSuper;

                if (allowChange) {
                    return (
                        <MentorDropdown
                            users={params.userList}
                            selectedUserId={data.connectToDonorAssignedUserId}
                            onSelect={(id: number) => params.onAssignedCounselorChange(data.id, id)}
                            onToggle={handleMenuToggle}
                        />
                    )
                }

                return <>{data.connectToDonorAssigndUser != null ? data.connectToDonorAssigndUser.firstName + ' ' + data.connectToDonorAssigndUser.lastName : ''}</>
            },
            sortable: true,
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IDonorConnect;
                return data.connectToDonorAssigndUser != null ? data.connectToDonorAssigndUser.lastName : '';
            }
        },
        {
            colId: 'hasDuplicate',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Dupes</SortableHeaderComponent>,
            width: 70,
            cellRendererParams: { onShowDuplicates: props.onShowDuplicates },
            cellRendererFramework: (params: IDupesRendererProps) => {
                let data = params.data as IDonorConnect;

                if (data.hasDuplicate == true) {
                    return (
                        <div style={{ paddingTop: 4 }}>
                            <Glyphicon glyph="duplicate" style={{ fontSize: 16 }} onClick={() => params.onShowDuplicates(data.id)} />
                        </div>
                    );
                }

                return <div>---</div>
            },
            valueGetter: (params: ValueGetterParams) => params.data.hasDuplicate ? 1 : 0
        },
        {
            colId: 'location',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Location</SortableHeaderComponent>,
            width: 180,
            comparator: Utility.nameComparator,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IDonorConnect;
                let isInternational = data.isResidingInUsa === false; //nullable truthy nightmares (consider null/undefined to reside in USA)

                if (isInternational) {
                    return <Label bsStyle='warning' style={{ fontSize: '100%' }}>{params.value}</Label>;
                } else {
                    return <div>{data.state}</div>;
                }
            },
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IDonorConnect;
                let isInternational = data.isResidingInUsa === false;
                let hasLocale = data.state;
                let v = hasLocale ? `${data.state}` : isInternational ? '[Not US Resident]' : '[Pending Demographics]';
                return v;
            }
        },
        {
            colId: 'timezone',
            field: 'timezone',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Timezone</SortableHeaderComponent>,
            width: 85,
        },
        {
            colId: 'centerAlias',
            field: 'centerAlias',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Selected<br />Center</SortableHeaderComponent>,
            width: 155,
            valueGetter: (params: ValueGetterParams) => params.data.centerAlias ?? '',
            comparator: Utility.nameComparator
        }
    ];

    const columnDefs = useMemo(() => cols, [props.statusList]);
    const rowData = useMemo(() => props.list, [props.list]);

    return (
        <>
            <GridWrapper className='ag-theme-bootstrap'>
                <AgGridReact
                    defaultColDef={{
                        sortable: true,
                    }}
                    columnDefs={columnDefs}
                    rowData={rowData}
                    headerHeight={34}
                    rowHeight={35}
                    suppressCellSelection={true}
                    suppressContextMenu={true}
                    suppressMultiSort={false}
                    immutableData={true}
                    getRowNodeId={(data: IDonorConnect) => data.id.toString()}
                    noRowsOverlayComponentFramework={() => <div>No registrations found...</div>}
                    onGridReady={onGridReady}
                    onFirstDataRendered={onFirstDataRendered}
                    onCellClicked={(e: CellClickedEvent) => {
                        if (e.colDef.field != 'connectToDonorStatus' && e.colDef.field != 'connectToDonorAssignedUserId' && e.colDef.colId != 'hasDuplicate') {
                            let { id, centerId, recipientCenterId } = (e.data as IDonorConnect);
                            props.onViewRegistration(id, centerId, recipientCenterId);
                        }
                    }}
                    onGridSizeChanged={onGridSizeChanged}
                    onBodyScroll={onBodyScroll}
                />
            </GridWrapper>
            <div>{rowData.length} registrations...</div>
        </>
    )
}

export default Grid;